import { Box, Grid, Stack, DialogContent } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  getStageStatusByCurrentUser,
  ICandidateStageStatusPipelineSetting,
  resetToDefaultStageStatus,
  saveStageStatusByCurrentUser,
} from 'wisestep-api';
import { ControlledTextInput, Label, WsButton, WsColors, WsDialog } from 'wisestep-base';

import DefaultStageStatusPipeline from '../../../utils/DefaultStageStatusPipeline';
import { warning, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import HeaderBox from '../components/HeaderBox';
import StageStatusEditWindow from '../components/StageStatusEditWindow';
import {
  AddIconButton,
  DeleteIconButton,
  EditIconButton,
  SaveIconButton,
  ArchiveIconButton,
  UnarchiveIconButton,
} from '../components/StyledIconButtons';

import ConfirmationDialog from './ConfirmationDialog';
import DeleteConfirmationDialog from './DeleteConfirmation';
import { stageStatusPipelineType } from './helper';

const CustomizeStageStatus = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [dialog, setDialog] = useState(false);
  const [edit, setEdit] = useState(-1);
  const [showDialog, setShowDialog] = useState(false);
  const [type, setType] = useState<'reset' | 'clear'>();
  const [candidateStageStatusSettings, setCandidateStageStatusSettings] =
    useState<ICandidateStageStatusPipelineSetting>();
  const [candidateStageStatusSettingsBackup, setCandidateStageStatusSettingsBackup] =
    useState<ICandidateStageStatusPipelineSetting>();
  const [stageStatusPipelineData, setStageStatusPipelineData] =
    useState<stageStatusPipelineType[]>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editStageName, setEditStageName] = useState(false);
  const [openArchiveConfirm, setOpenArchiveConfirm] = useState(false);
  const methods = useForm();
  const parseData = (responseData: string) => {
    const data: stageStatusPipelineType[] = JSON.parse(responseData.replace(/'/g, '"'));
    data.map((stage) => {
      if (stage.isArchive === undefined) {
        stage.isArchive = false;
      }
      stage.statuses.map((status) => {
        if (status.isArchive === undefined) {
          status.isArchive = false;
        }
        if (status.mandatoryFields === undefined) {
          status.mandatoryFields = [];
        }
      });
    });
    return data;
  };
  useEffect(() => {
    (async function () {
      const getCandidateStageStatusSettings = await getStageStatusByCurrentUser();
      if (getCandidateStageStatusSettings.data) {
        setCandidateStageStatusSettings(getCandidateStageStatusSettings.data);
        setCandidateStageStatusSettingsBackup(getCandidateStageStatusSettings.data);
        const data: stageStatusPipelineType[] = parseData(
          getCandidateStageStatusSettings.data.stageStatusPipeline.replace(/'/g, '"')
        );
        setStageStatusPipelineData(data);
      } else {
        DefaultStageStatusPipeline.id = '';
        setCandidateStageStatusSettings(DefaultStageStatusPipeline);
        setCandidateStageStatusSettingsBackup(DefaultStageStatusPipeline);
        const data: stageStatusPipelineType[] = parseData(
          DefaultStageStatusPipeline.stageStatusPipeline.replace(/'/g, '"')
        );
        setStageStatusPipelineData(data);
      }
    })();
  }, []);

  const onStatusEdit = (stageIndex) => {
    setEdit(stageIndex);
    setDialog(true);
  };

  const openDialog = (type: 'reset' | 'clear') => {
    setType(type);
    setShowDialog(true);
  };

  const closeDialog = () => setShowDialog(false);
  const handler = async (type) => {
    if (candidateStageStatusSettings && candidateStageStatusSettingsBackup) {
      if (type === 'clear') {
        setCandidateStageStatusSettings({ ...candidateStageStatusSettingsBackup });
        const data: stageStatusPipelineType[] = parseData(
          candidateStageStatusSettingsBackup?.stageStatusPipeline?.replace(/'/g, '"') ?? ''
        );
        setStageStatusPipelineData(data);
        setShowDialog(false);
      } else {
        try {
          const restToDefaultStageStatus = await resetToDefaultStageStatus();
          setCandidateStageStatusSettings(restToDefaultStageStatus.data);
          const data: stageStatusPipelineType[] = parseData(
            restToDefaultStageStatus.data.stageStatusPipeline.replace(/'/g, '"')
          );
          setStageStatusPipelineData(data);
          setShowDialog(false);
          setNotification(success('Reset To Default Stage Status successful'));
        } catch (error) {
          // setNotification(warning('Something Went Wrong , Please try again'));
          setCandidateStageStatusSettings(DefaultStageStatusPipeline);
          // setCandidateStageStatusSettingsBackup(DefaultStageStatusPipeline);
          const data: stageStatusPipelineType[] = parseData(
            DefaultStageStatusPipeline.stageStatusPipeline.replace(/'/g, '"')
          );
          setStageStatusPipelineData(data);
          setShowDialog(false);
          setNotification(success('Reset To Default Stage Status successful'));
        }
      }
    }
  };
  const saveToApi = async () => {
    if (candidateStageStatusSettings && stageStatusPipelineData) {
      const payload: ICandidateStageStatusPipelineSetting = candidateStageStatusSettings;
      payload.stageStatusPipeline = JSON.stringify(stageStatusPipelineData);
      payload.recruiterId = payload.createdBy;
      try {
        await saveStageStatusByCurrentUser(payload);
        setNotification(success('Configure Job Attributes,Settings updated successfully'));
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
    }
  };
  const addNewStage = (stageIndex) => {
    if (stageStatusPipelineData) {
      stageStatusPipelineData.splice(stageIndex + 1, 0, {
        stageOrder: stageIndex + 1,
        stage: 'New Stage',
        statuses: [],
        isArchive: false,
      });
      setStageStatusPipelineData([...stageStatusPipelineData]);
    }
  };

  const restoreStage = (stageIndex, restoreStage, restoreArchive) => {
    if (stageStatusPipelineData) {
      stageStatusPipelineData.splice(restoreArchive, 1);
      stageStatusPipelineData.splice(stageIndex + 1, 0, { ...restoreStage, isArchive: false });
      stageStatusPipelineData.map((data, index) => (data.stageOrder = index));
      setStageStatusPipelineData([...stageStatusPipelineData]);
    }
  };

  const onStageEditSave = (data) => {
    if (stageStatusPipelineData && data.editStageName) {
      stageStatusPipelineData[edit].stage = data.editStageName;
      setStageStatusPipelineData([...stageStatusPipelineData]);
    }
    setEditStageName(false);
    setEdit(-1);
    methods.reset();
  };

  const onDeleteStage = () => {
    if (stageStatusPipelineData) {
      stageStatusPipelineData.splice(edit, 1);
      setStageStatusPipelineData([...stageStatusPipelineData]);
      setConfirmDelete(false);
      setEdit(-1);
    }
  };

  const onArchive = (archive: boolean, restoreArchive: number) => {
    if (stageStatusPipelineData && archive) {
      const archiveStage = stageStatusPipelineData[restoreArchive];
      stageStatusPipelineData.splice(restoreArchive, 1);
      archiveStage.isArchive = archive;
      stageStatusPipelineData.push(archiveStage);
      stageStatusPipelineData.map((data, index) => (data.stageOrder = index));
      setStageStatusPipelineData([...stageStatusPipelineData]);
    } else if (stageStatusPipelineData && restoreArchive) {
      restoreStage(
        stageStatusPipelineData.filter((element) => !element.isArchive).length - 1,
        stageStatusPipelineData[restoreArchive],
        restoreArchive
      );
    }
    setEditStageName(false);
    setEdit(-1);
    setOpenArchiveConfirm(false);
    setConfirmDelete(false);
    methods.reset();
  };

  return (
    <>
      <FormProvider {...methods}>
        <Box px={8} pt={2} display="flex" flexDirection="column">
          <HeaderBox
            title="Candidate Stage Status Pipeline Management"
            includeBtn={true}
            btnText="Add New Stage"
            onClick={() => addNewStage(-1)}
          />
          <Stack mb={2} justifyContent="end" display="flex" direction="row" spacing={2}>
            <WsButton onClick={() => openDialog('clear')} variant="contained">
              Clear Unsaved Changes
            </WsButton>
            <WsButton onClick={() => openDialog('reset')} variant="contained">
              Reset to Default
            </WsButton>
            <WsButton onClick={() => saveToApi()} variant="contained">
              Save Everything
            </WsButton>
          </Stack>
          <Grid
            container
            mb={2}
            // key={stageStatusPipelineData.indexOf(stageElement)}
            justifyContent="space-between"
          >
            {stageStatusPipelineData
              ?.filter((stageElement) => !stageElement.isArchive)
              ?.map((stageElement, index) => (
                <>
                  <StatusDetails
                    stageElement={stageElement}
                    stageStatusPipelineData={stageStatusPipelineData}
                    editStageName={editStageName}
                    edit={edit}
                    onStatusEdit={onStatusEdit}
                    index={index}
                  />
                  <Grid item xs={2} display="flex" justifyContent="end">
                    {stageElement.stage != 'Joining' && (
                      <ArchiveIconButton
                        onClick={() => {
                          setEdit(stageStatusPipelineData.indexOf(stageElement));
                          onArchive(true, stageStatusPipelineData.indexOf(stageElement));
                        }}
                      />
                    )}
                    {editStageName && stageStatusPipelineData.indexOf(stageElement) === edit ? (
                      <SaveIconButton onClick={methods.handleSubmit(onStageEditSave)} />
                    ) : (
                      stageElement.stage != 'Joining' && (
                        <EditIconButton
                          onClick={() => {
                            setEdit(stageStatusPipelineData.indexOf(stageElement));
                            // setEditStageName(true);
                            setOpenArchiveConfirm(true);
                          }}
                        />
                      )
                    )}
                    <AddIconButton
                      onClick={() => addNewStage(stageStatusPipelineData.indexOf(stageElement))}
                    />
                    {stageElement.stage != 'Joining' && (
                      <DeleteIconButton
                        onClick={() => {
                          setEdit(stageStatusPipelineData.indexOf(stageElement));
                          setConfirmDelete(true);
                          setOpenArchiveConfirm(true);
                        }}
                      />
                    )}
                  </Grid>
                </>
              ))}
            <Grid item xs={12} mt={2}>
              <Label
                sx={{
                  fontWeight: 'bold',
                  minWidth: '100%',
                  ml: 2,
                }}
              >
                Archived Status
              </Label>
            </Grid>
            {stageStatusPipelineData
              ?.filter((stageElement) => stageElement.isArchive)
              ?.map((stageElement, index) => (
                <>
                  <StatusDetails
                    stageElement={stageElement}
                    stageStatusPipelineData={stageStatusPipelineData}
                    editStageName={editStageName}
                    edit={edit}
                    onStatusEdit={onStatusEdit}
                    index={index}
                  />
                  <Grid item xs={2} display="flex" justifyContent="end">
                    <UnarchiveIconButton
                      onClick={() => {
                        setEdit(stageStatusPipelineData.indexOf(stageElement));
                        onArchive(false, stageStatusPipelineData.indexOf(stageElement));
                      }}
                    />
                    <DeleteIconButton
                      onClick={() => {
                        setEdit(stageStatusPipelineData.indexOf(stageElement));
                        setConfirmDelete(true);
                        setOpenArchiveConfirm(false);
                      }}
                    />
                  </Grid>
                </>
              ))}
          </Grid>
          {edit !== -1 && stageStatusPipelineData && (
            <StageStatusEditWindow
              editWindow={dialog}
              setEditWindow={setDialog}
              stageData={stageStatusPipelineData[edit]}
              setStatusData={(newStageStatus) => {
                stageStatusPipelineData[edit].statuses = newStageStatus;
                setStageStatusPipelineData([...stageStatusPipelineData]);
              }}
              setEditStage={setEdit}
            />
          )}

          {confirmDelete && !openArchiveConfirm && (
            <WsDialog openDialog={true} size="small">
              <DialogContent>
                <Grid container spacing={1} direction="column">
                  <Grid item xs={12}>
                    <Label sx={{ fontWeight: 'bold' }}>
                      Are you sure you want to delete this stage?
                    </Label>
                  </Grid>
                  <Grid
                    item
                    width="40%"
                    display="flex"
                    justifyContent="space-between"
                    alignSelf="center"
                  >
                    <WsButton onClick={onDeleteStage}>Yes</WsButton>
                    <WsButton
                      onClick={() => {
                        setEdit(-1);
                        setConfirmDelete(false);
                      }}
                    >
                      No
                    </WsButton>
                  </Grid>
                </Grid>
              </DialogContent>
            </WsDialog>
          )}
          {edit !== -1 && openArchiveConfirm && (
            <DeleteConfirmationDialog
              openDialog={openArchiveConfirm}
              isEdit={edit !== -1 && !confirmDelete}
              closeDialog={() => {
                setEdit(-1);
                setConfirmDelete(false);
              }}
              onArchive={(value) => onArchive(value, edit)}
              handler={(isEditStatus) => {
                if (isEditStatus) {
                  setEditStageName(true);
                } else {
                  setConfirmDelete(true);
                }
                setOpenArchiveConfirm(false);
              }}
            />
          )}
        </Box>
      </FormProvider>
      <ConfirmationDialog
        openDialog={showDialog}
        closeDialog={() => closeDialog()}
        handler={(type) => handler(type)}
        variant={type ?? 'clear'}
      />
    </>
  );
};

export default CustomizeStageStatus;

type StatusDetailsType = {
  stageElement: stageStatusPipelineType;
  stageStatusPipelineData: stageStatusPipelineType[];
  onStatusEdit: (index: number) => void;
  editStageName: boolean;
  edit: number;
  index: number;
};
const StatusDetails = ({
  stageElement,
  stageStatusPipelineData,
  onStatusEdit,
  editStageName,
  edit,
  index,
}: StatusDetailsType) => (
  <>
    <Grid item xs={1} display="flex" alignItems="center" ml={3}>
      {!stageElement.isArchive && (
        <Label
          sx={{
            fontWeight: 'bold',
            color: WsColors.TheBlues500,
            minWidth: '100%',
            textAlign: 'center',
          }}
        >
          Stage {index}
        </Label>
      )}
    </Grid>

    <Grid item xs={4.5} display="flex" justifyContent="center" alignItems="center" pr={2}>
      {editStageName && stageStatusPipelineData.indexOf(stageElement) === edit ? (
        <ControlledTextInput name="editStageName" defaultValue={stageElement.stage} />
      ) : (
        <Label sx={{ fontWeight: 'bold', minWidth: '100%', textAlign: 'center' }}>
          {stageElement.stage}
        </Label>
      )}
    </Grid>

    <Grid item xs={4} display="flex" alignItems="center">
      <WsButton
        fullWidth
        variant="outlined"
        onClick={() => onStatusEdit(stageStatusPipelineData.indexOf(stageElement))}
      >
        {`Show Status List (${stageElement?.statuses.length})`}
      </WsButton>
    </Grid>
  </>
);
